/**
 * ActionRegistry
 *
 * Central registry for fast action configurations.
 * Maps action types to their icons, colors, target entities, and forms.
 *
 * This is the single source of truth for fast action behavior.
 */

import {
  Phone,
  Video,
  Mail,
  MessageCircle,
  CheckSquare,
  Calendar,
  Paperclip,
  FileText,
  StickyNote,
  Bell,
  type LucideIcon,
} from 'lucide-react';
import { type ActionType, STANDARD_FAST_ACTIONS } from '../../../types/row.types';
import type { IdPrefix } from '@business-assistant/shared/domain';
import type { ComponentType } from 'react';

// =============================================================================
// ACTION CONFIGURATION
// =============================================================================

/**
 * Configuration for a single action type
 */
export interface ActionConfig {
  /** Action identifier */
  id: ActionType;

  /** Display label (Italian) */
  label: string;

  /** Icon component */
  icon: LucideIcon;

  /** Color when action is linked (hex) */
  color: string;

  /** Target entity prefix when action creates new entity */
  targetPrefix: IdPrefix;

  /** Form component to open (will be populated from FormRegistry) */
  formComponent?: ComponentType<any>;
}

/**
 * Action configurations registry
 */
export const ACTION_CONFIGS: Record<ActionType, ActionConfig> = {
  call: {
    id: 'call',
    label: 'Call',
    icon: Phone,
    color: '#1D5BBF',
    targetPrefix: 'cal',
  },
  videocall: {
    id: 'videocall',
    label: 'Videocall',
    icon: Video,
    color: '#7C3AED',
    targetPrefix: 'vcl',
  },
  email: {
    id: 'email',
    label: 'Email',
    icon: Mail,
    color: '#FF6B2D',
    targetPrefix: 'eml',
  },
  message: {
    id: 'message',
    label: 'Message',
    icon: MessageCircle,
    color: '#00C853',
    targetPrefix: 'msg',
  },
  task: {
    id: 'task',
    label: 'Task',
    icon: CheckSquare,
    color: '#00A803',
    targetPrefix: 'tsk',
  },
  event: {
    id: 'event',
    label: 'Event',
    icon: Calendar,
    color: '#9B59B6',
    targetPrefix: 'cal', // Using call prefix for now
  },
  file: {
    id: 'file',
    label: 'File',
    icon: Paperclip,
    color: '#607D8B',
    targetPrefix: 'fil',
  },
  invoice: {
    id: 'invoice',
    label: 'Invoice',
    icon: FileText,
    color: '#F59E0B',
    targetPrefix: 'inv',
  },
  note: {
    id: 'note',
    label: 'Nota',
    icon: StickyNote,
    color: '#FFC107',
    targetPrefix: 'not',
  },
  reminder: {
    id: 'reminder',
    label: 'Promemoria',
    icon: Bell,
    color: '#EF4444',
    targetPrefix: 'evt', // Reminders are stored as calendar events
  },
};

// =============================================================================
// ENTITY → AVAILABLE ACTIONS MAPPING
// =============================================================================

/**
 * Standard fast actions used across ALL entity rows and chat messages.
 *
 * The fast actions are ALWAYS the same everywhere:
 * task, event, email, call, message, reminder, note
 *
 * This ensures a consistent user experience across the entire platform.
 */
export const DEFAULT_FAST_ACTIONS: ActionType[] = [...STANDARD_FAST_ACTIONS];

/**
 * Maps entity types to their available fast actions.
 * By default, ALL entities use STANDARD_FAST_ACTIONS.
 *
 * Override only if an entity type needs ADDITIONAL actions beyond the standard set.
 */
export const ENTITY_ADDITIONAL_ACTIONS: Partial<Record<IdPrefix, ActionType[]>> = {
  // Projects module - add file and invoice
  prj: ['file', 'invoice'],

  // Hub contacts - add videocall
  cnt: ['videocall'],

  // Accounting module - add invoice and file
  inv: ['file'],
  mov: ['invoice'],
};

// =============================================================================
// REGISTRY FUNCTIONS
// =============================================================================

/**
 * Gets configuration for an action type
 *
 * @param actionType - Action type
 * @returns Action configuration
 *
 * @example
 * ```tsx
 * const config = getActionConfig('email');
 * console.log(config.label); // => 'Email'
 * console.log(config.color); // => '#FF6B2D'
 * ```
 */
export function getActionConfig(actionType: ActionType): ActionConfig {
  return ACTION_CONFIGS[actionType];
}

/**
 * Gets available actions for an entity type.
 *
 * Returns the STANDARD_FAST_ACTIONS plus any additional entity-specific actions.
 * This ensures consistent fast actions across the entire platform.
 *
 * @param entityId - Entity ID (prefix will be extracted)
 * @returns Array of available action types (standard + additional)
 *
 * @example
 * ```tsx
 * const actions = getAvailableActions('tsk-123');
 * // => ['task', 'event', 'email', 'call', 'message', 'reminder', 'note']
 *
 * const projectActions = getAvailableActions('prj-456');
 * // => ['task', 'event', 'email', 'call', 'message', 'reminder', 'note', 'file', 'invoice']
 * ```
 */
export function getAvailableActions(entityId: string): ActionType[] {
  const prefix = entityId.split('-')[0] as IdPrefix;
  const additionalActions = ENTITY_ADDITIONAL_ACTIONS[prefix] || [];

  // Return standard actions + any additional entity-specific actions
  return [...DEFAULT_FAST_ACTIONS, ...additionalActions];
}

/**
 * Gets icon color for an action based on link status
 *
 * @param actionType - Action type
 * @param isLinked - Whether action has linked entities
 * @returns Hex color code
 *
 * @example
 * ```tsx
 * const color = getActionColor('email', true);
 * // => '#FF6B2D' (linked, shows configured color)
 *
 * const grayColor = getActionColor('email', false);
 * // => '#aaa' (not linked, shows gray)
 * ```
 */
export function getActionColor(actionType: ActionType, isLinked: boolean): string {
  const config = ACTION_CONFIGS[actionType];
  return isLinked ? config.color : '#aaa';
}

/**
 * Gets all action types
 *
 * @returns Array of all action types
 */
export function getAllActionTypes(): ActionType[] {
  return Object.keys(ACTION_CONFIGS) as ActionType[];
}

/**
 * Gets all actions with their full configurations
 *
 * @returns Array of action configurations
 */
export function getAllActions(): ActionConfig[] {
  return Object.values(ACTION_CONFIGS);
}

/**
 * Checks if an entity type has any fast actions
 *
 * @param prefix - Entity ID prefix
 * @returns true if entity has actions (all entities have standard fast actions)
 */
export function hasActions(prefix: IdPrefix): boolean {
  // All entities now have standard fast actions
  return true;
}

/**
 * Gets action configuration by target prefix
 * Useful for reverse lookup (e.g., "which action creates emails?")
 *
 * @param targetPrefix - Target entity prefix
 * @returns Array of actions that create this entity type
 *
 * @example
 * ```tsx
 * const emailActions = getActionsByTarget('eml');
 * // => [{ id: 'email', label: 'Email', ... }]
 * ```
 */
export function getActionsByTarget(targetPrefix: IdPrefix): ActionConfig[] {
  return Object.values(ACTION_CONFIGS).filter(
    (config) => config.targetPrefix === targetPrefix
  );
}
